import { BrowserModule } from '@angular/platform-browser';
import { NgModule, isDevMode } from '@angular/core';
import { ServiceWorkerModule } from '@angular/service-worker';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { LoginComponent } from './login/login.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MaterialModule } from './material.module';
import { ColorPickerModule } from 'ngx-color-picker';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { ToastrModule } from 'ngx-toastr';
import { NgxSpinnerModule } from 'ngx-spinner';
import { BsDatepickerModule, BsLocaleService } from 'ngx-bootstrap/datepicker';
import { ModalModule } from 'ngx-bootstrap/modal';
import { TimepickerModule } from 'ngx-bootstrap/timepicker';
import { defineLocale } from 'ngx-bootstrap/chronos';
import { trLocale } from 'ngx-bootstrap/locale';
import { CommonModule, DatePipe, APP_BASE_HREF } from '@angular/common';
import { MAT_CHECKBOX_DEFAULT_OPTIONS } from '@angular/material/checkbox';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';

// Services and constants
import { WORK_START_DATE_FORMAT } from './_utils/constants';
import { InterceptorService } from './_services/interceptor.service';
import { JwtInterceptor } from './_services/jwt.interceptor.service';
import { LoaderInterceptor } from './_helpers/loader.interceptor';

// Components
import { ServerdownComponent } from './serverdown/serverdown.component';
import { RoleselectComponent } from './roleselect/roleselect.component';
import { RequestsComponent } from './requests/requests.component';
import { ProfileComponent } from './profile/profile.component';
import { LoaderComponent } from './loader/loader.component';
import { LookupComponent } from './lookup/lookup.component';
import { PortaluserListComponent } from './portaluser/portaluser-list/portaluser-list.component';
import { PortaluserDeleteComponent } from './portaluser/portaluser-delete/portaluser-delete.component';
import { PortaluserAddComponent } from './portaluser/portaluser-add/portaluser-add.component';

// Absence Components
import { AbsenceComponent } from './absence/absence.component';
import { AbsenceapproveComponent } from './absence/absenceapprove/absenceapprove.component';
import { AbsencerejectComponent } from './absence/absencereject/absencereject.component';
import { RejectselectedComponent } from './absence/rejectselected/rejectselected.component';

// Overtime Components
import { OvertimeComponent } from './overtime/overtime.component';
import { OvertimeUpdateComponent } from './overtime/overtime-update/overtime-update.component';
import { OvertimeDetailComponent } from './overtime/overtime-detail/overtime-detail.component';
import { OvertimeApproveComponent } from './overtime/overtime-approve/overtime-approve.component';
import { OvertimeRejectComponent } from './overtime/overtime-reject/overtime-reject.component';
import { OvertimeDeleteComponent } from './overtime/overtime-delete/overtime-delete.component';
import { OvertimeApproveSelectedComponent } from './overtime/overtime-approve-selected/overtime-approve-selected.component';
import { OvertimeRejectSelectedComponent } from './overtime/overtime-reject-selected/overtime-reject-selected.component';

// Request Components
import { RequestsApproveComponent } from './requests/requestsApprove/requestsApprove.component';
import { RequestrejectComponent } from './requests/requestsreject/requestsreject.component';
import { RequestsCompleteComponent } from './requests/requestsComplete/requestsComplete.component';
import { RequestsCompleteSelectedComponent } from './requests/requestsCompleteSelected/requestsCompleteSelected';
import { RequestsdetailComponent } from './requests/detail/requestsdetail.component';
import { RequestsCancelComponent } from './requests/requestsCancel/requestsCancel.component';
import { RequestsCancelSelectedComponent } from './requests/requestsCancelSelected/requestsCancelSelected.component';
import { RequestApproveSelected } from './requests/requestsApproveSelected/requestsApproveSelected.component';
import { RequestsRejectSelected } from './requests/requestsRejectSelected/requestsRejectSelected.component';
import { AbsencedetailComponent } from './absence/detail/absencedetail.component';
import { ProcurementComponent } from './procurement/procurement.component';
import { ItemAuthorizationsComponent } from './item-authorizations/item-authorizations.component';

@NgModule({
  declarations: [],
  imports: [
    CommonModule,
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    MaterialModule,
    FontAwesomeModule,
    ToastrModule.forRoot(),
    NgxSpinnerModule,
    ColorPickerModule,
    BsDatepickerModule.forRoot(),
    TimepickerModule.forRoot(),
    ModalModule.forRoot(),
    
    // Standalone Components
    AppComponent,
    LoginComponent,
    ServerdownComponent,
    RoleselectComponent,
    RequestsComponent,
    ProfileComponent,
    LoaderComponent,
    LookupComponent,
    PortaluserListComponent,
    PortaluserDeleteComponent,
    PortaluserAddComponent,
    AbsenceComponent,
    AbsenceapproveComponent,
    AbsencerejectComponent,
    AbsencedetailComponent,
    RejectselectedComponent,
    OvertimeComponent,
    OvertimeUpdateComponent,
    OvertimeDetailComponent,
    OvertimeApproveComponent,
    OvertimeRejectComponent,
    OvertimeDeleteComponent,
    OvertimeApproveSelectedComponent,
    OvertimeRejectSelectedComponent,
    RequestsApproveComponent,
    RequestsRejectSelected,
    RequestrejectComponent,
    RequestsCompleteComponent,
    RequestsCompleteSelectedComponent,
    RequestsCancelComponent,
    RequestsCancelSelectedComponent,
    RequestsdetailComponent,
    RequestApproveSelected,
    ProcurementComponent,
    ItemAuthorizationsComponent
  ],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: InterceptorService, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: LoaderInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: MAT_CHECKBOX_DEFAULT_OPTIONS, useValue: 'check' },
    { provide: MAT_DATE_LOCALE, useValue: 'tr-TR' },
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
    { provide: MAT_DATE_FORMATS, useValue: WORK_START_DATE_FORMAT },
    DatePipe
  ]
})
export class AppModule {
  constructor(private localeService: BsLocaleService) {
    defineLocale('tr', trLocale);
    this.localeService.use('tr');
  }
}
